
import { getCookie } from "@/lib/auth/cookies";
import {
  getOnboardingProgress,
  completeOnboardingStep,
  updateOnboardingState,
  resetOnboarding as resetOnboardingApi,
} from "@/lib/api/clients/OnboardingClient";
import { handleErrorServer, ServerError } from "@/lib/api/errors/ErrorHandler";
import { OnboardingProgress } from "@/types/OnboardingProgress";
import { isValidObjectId } from "@/lib/utils/input-validator";

export async function getOnboarding(): Promise<
  OnboardingProgress | ServerError
> {
  try {
    const companyId = await getCookie("companyId");

    if (!isValidObjectId(companyId ?? "")) throw new Error("validationError");

    return await getOnboardingProgress(companyId!);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function completeStep(
  stepKey: string,
): Promise<void | ServerError> {
  try {
    const companyId = await getCookie("companyId");

    if (!isValidObjectId(companyId ?? "")) throw new Error("validationError");
    if (!stepKey || stepKey.trim().length < 1)
      throw new Error("validationError");

    await completeOnboardingStep(companyId!, stepKey);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function setOnboardingState(
  dismissed?: boolean,
  minimized?: boolean,
  welcomeShown?: boolean,
  tourState?: "PENDING" | "COMPLETED" | "SKIPPED",
): Promise<void | ServerError> {
  try {
    const companyId = await getCookie("companyId");

    if (!isValidObjectId(companyId ?? "")) throw new Error("validationError");
    if (
      dismissed === undefined &&
      minimized === undefined &&
      welcomeShown === undefined &&
      tourState === undefined
    )
      throw new Error("validationError");

    await updateOnboardingState(companyId!, {
      dismissed,
      minimized,
      welcomeShown,
      tourState,
    });
  } catch (error) {
    return handleErrorServer(error);
  }
}

/** Mark the welcome modal as shown (once). */
export async function setWelcomeShown(): Promise<void | ServerError> {
  return setOnboardingState(undefined, undefined, true);
}

/** Persist the spotlight-tour outcome. */
export async function setTourState(
  tourState: "PENDING" | "COMPLETED" | "SKIPPED",
): Promise<void | ServerError> {
  return setOnboardingState(undefined, undefined, undefined, tourState);
}

export async function resetOnboarding(): Promise<void | ServerError> {
  try {
    const companyId = await getCookie("companyId");

    if (!isValidObjectId(companyId ?? "")) throw new Error("validationError");

    await resetOnboardingApi(companyId!);
  } catch (error) {
    return handleErrorServer(error);
  }
}
